"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import { Check, Copy, ExternalLink, X } from "lucide-react"

import { useLocale } from "@/lib/locale-context"
import type { ProjectDetail } from "@/lib/project-details"
import type { Project } from "@/lib/projects"
import { cn } from "@/lib/utils"
import { NicoRide } from "@/components/nico-ride"
import { ProjectCarousel } from "@/components/project-carousel"
import { SectionHead } from "@/components/section-head"

const carouselLabels = {
  en: { previous: "Previous screen", next: "Next screen" },
  pt: { previous: "Ecrã anterior", next: "Ecrã seguinte" },
  de: { previous: "Vorheriger Screen", next: "Nächster Screen" },
} as const

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="t-small absolute right-3 top-3 inline-flex items-center gap-1.5 border border-border bg-background px-3 py-1.5 text-muted-foreground transition-colors hover:text-foreground"
      onClick={() => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
    >
      {copied ? <Check className="h-3 w-3 text-accent" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : label}
    </button>
  )
}

function Section({
  index,
  title,
  children,
}: {
  index: string
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="py-14 md:py-20" data-nico-stop>
      <div className="shell">
        <SectionHead id={title.toLowerCase().replace(/\s+/g, "-")} index={index} title={title} />
        <div className="mt-8">{children}</div>
      </div>
    </section>
  )
}

function Terminal({
  label,
  code,
  copyLabel,
}: {
  label: string
  code: string
  copyLabel: string
}) {
  return (
    <div className="relative overflow-hidden border border-border">
      <div className="border-b border-border px-4 py-2.5">
        <span className="t-small font-mono text-muted-foreground">{label}</span>
      </div>
      <div className="relative p-4">
        <CopyButton text={code} label={copyLabel} />
        <pre className="overflow-x-auto pr-24 font-mono text-[13px] leading-relaxed text-foreground">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  )
}

export function ProjectDetailPage({
  project,
  detail,
}: {
  project: Project
  detail: ProjectDetail
}) {
  const { locale, t } = useLocale()
  const labels = detail.labels[locale]
  const repoLabel = detail.repoLabelOverride?.[locale] ?? labels.viewSource
  /** The image open full screen, if any. */
  const [lightbox, setLightbox] = useState<string | null>(null)
  const gettingStartedInfo = detail.gettingStartedInfo?.[locale]
  const statusLabel =
    project.status === "active"
      ? t.projects.statusActive
      : project.status === "in-progress"
        ? t.projects.statusInProgress
        : t.projects.statusArchived
  const shot = detail.screenshotImg ?? project.coverImg
  const gallery = detail.gallery ?? []

  // Sections are numbered in the order they appear, whichever ones a project has.
  const order = [
    "about",
    detail.features.length > 0 && "features",
    detail.dockerRun && "gettingStarted",
    detail.apiEndpoints && "api",
    detail.cliCommands && "cli",
  ].filter(Boolean) as string[]
  const index = (key: string) => String(order.indexOf(key) + 1).padStart(2, "0")

  useEffect(() => {
    if (!lightbox) return
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setLightbox(null)
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [lightbox])

  return (
    <div className="relative">
      {detail.companion === "nico" && <NicoRide />}

      {/* ---- Masthead ---- */}
      <section className="pt-12 md:pt-16">
        <div className="shell">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <span className="t-small inline-flex items-center gap-2 text-muted-foreground">
                <span
                  className={cn(
                    "inline-block h-2 w-2",
                    project.status === "active" && "bg-accent",
                    project.status === "in-progress" && "border border-accent",
                    project.status === "archived" && "bg-muted-foreground/50"
                  )}
                  aria-hidden="true"
                />
                {statusLabel}
              </span>

              <h1 className="t-xl mt-4 text-balance uppercase text-foreground">
                {project.title[locale]}
              </h1>
              <p className="t-md mt-4 max-w-[34ch] text-accent">
                {detail.tagline[locale]}
              </p>

              {detail.repoUrl && (
                <a
                  href={detail.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="t-small mt-8 inline-flex items-center gap-2 bg-foreground px-6 py-3 font-medium uppercase tracking-[0.06em] text-background transition-colors hover:bg-accent"
                >
                  {repoLabel}
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>

            <div className="lg:col-span-4 lg:col-start-9">
              <ul className="flex flex-wrap gap-2 lg:justify-end">
                {project.tech.map((item) => (
                  <li
                    key={item}
                    className="t-small border border-border px-3 py-1 text-muted-foreground"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {gallery.length > 0 ? (
            <ProjectCarousel
              slides={gallery.map((image) => ({ src: image.src, caption: image.caption[locale] }))}
              onOpen={setLightbox}
              labels={{ ...carouselLabels[locale], region: labels.gallery }}
            />
          ) : shot && (
            <button
              type="button"
              onClick={() => setLightbox(shot)}
              className="group mt-12 block w-full overflow-hidden border border-border"
              aria-label={labels.screenshot}
            >
              <Image
                src={shot}
                alt={`${project.title[locale]} ${labels.screenshot}`}
                width={3440}
                height={2150}
                priority
                sizes="(max-width: 1200px) 100vw, 1200px"
                className="aspect-[16/9] w-full object-cover object-top"
              />
            </button>
          )}
        </div>
      </section>

      {/* ---- About ---- */}
      <Section index={index("about")} title={labels.about}>
        <div className="grid12 gap-y-6">
          {detail.longDescription[locale].split("\n\n").map((paragraph, i) => (
            <p
              key={i}
              className="t-body col-span-12 max-w-[58ch] text-muted-foreground md:col-span-6"
            >
              {paragraph}
            </p>
          ))}
        </div>
      </Section>

      {/* ---- Features ---- */}
      {detail.features.length > 0 && (
      <Section index={index("features")} title={labels.features}>
        <dl className="grid grid-cols-1 gap-x-10 md:grid-cols-2">
          {detail.features.map((feature) => (
            <div key={feature.title[locale]} className="rule py-6">
              <dt className="t-md text-foreground">
                {feature.title[locale]}
              </dt>
              <dd className="t-body mt-2 max-w-[46ch] text-muted-foreground">
                {feature.description[locale]}
              </dd>
            </div>
          ))}
        </dl>
      </Section>
      )}

      {/* ---- Getting started ---- */}
      {detail.dockerRun && (
        <Section index={index("gettingStarted")} title={labels.gettingStarted}>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <Terminal
                label={labels.docker}
                code={detail.dockerRun}
                copyLabel={labels.quickStart}
              />
            </div>
            {gettingStartedInfo && (
              <dl className="lg:col-span-3 lg:col-start-10">
                {gettingStartedInfo.split("\n").map((line) => {
                  const [key, ...rest] = line.split(":")
                  return (
                    <div key={line} className="flex gap-4 border-t border-border py-3">
                      <dt className="text-[13px] text-muted-foreground">{key}</dt>
                      <dd className="font-mono text-[12.5px] text-foreground">
                        {rest.join(":").trim()}
                      </dd>
                    </div>
                  )
                })}
              </dl>
            )}
          </div>
        </Section>
      )}

      {/* ---- API ---- */}
      {detail.apiEndpoints && (
        <Section index={index("api")} title={labels.api}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[34rem] border-collapse text-left">
              <thead>
                <tr className="border-b border-border">
                  <th className="t-small py-2.5 pr-4 font-normal text-muted-foreground">
                    {labels.apiMethod}
                  </th>
                  <th className="t-small py-2.5 pr-4 font-normal text-muted-foreground">
                    {labels.apiPath}
                  </th>
                  <th className="t-small py-2.5 font-normal text-muted-foreground">
                    {labels.apiDescription}
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.apiEndpoints.map((endpoint) => (
                  <tr
                    key={`${endpoint.method}-${endpoint.path}`}
                    className="border-b border-border/70"
                  >
                    <td className="py-3 pr-4 align-top">
                      <span
                        className={cn(
                          "font-mono text-[12px] font-medium",
                          endpoint.method === "GET" && "text-accent",
                          endpoint.method === "POST" && "text-foreground",
                          endpoint.method === "PUT" && "text-accent"
                        )}
                      >
                        {endpoint.method}
                      </span>
                    </td>
                    <td className="py-3 pr-4 align-top font-mono text-[12.5px] text-foreground">
                      {endpoint.path}
                    </td>
                    <td className="t-small py-3 align-top text-muted-foreground">
                      {endpoint.description[locale]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* ---- CLI ---- */}
      {detail.cliCommands && (
        <Section index={index("cli")} title={labels.cli}>
          <div className="lg:max-w-3xl">
            <Terminal
              label={detail.cliLabel ?? labels.cli}
              code={detail.cliCommands}
              copyLabel={labels.cli}
            />
          </div>
        </Section>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 h-full w-full cursor-default"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          />
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-6 top-6 z-10 flex h-9 w-9 items-center justify-center border border-foreground bg-background text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="relative z-10 w-full max-w-6xl">
            <Image
              src={lightbox}
              alt={
                gallery.find((image) => image.src === lightbox)?.caption[locale] ??
                `${project.title[locale]} ${labels.screenshot}`
              }
              width={3440}
              height={2150}
              sizes="100vw"
              className="h-auto w-full border border-border"
            />
          </div>
        </div>
      )}
    </div>
  )
}
